import { useEffect, useRef, useState } from 'react';
import {
  Card, CardContent, Stepper, Step, StepLabel, Button, Stack, TextField, MenuItem, Chip, Alert, Box, Typography,
  IconButton, FormControlLabel, Checkbox, Snackbar, CircularProgress, Divider,
} from '@mui/material';
import { Add, Delete, Save, Send, Close } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { FileUploadField } from '../../../components/FileUploadField';
import {
  technicianOnboardingApi, TECH_TYPES, GENDERS, SKILL_CATEGORIES, EXPERIENCE_LEVELS, SERVICE_COVERAGES,
  EMPLOYMENT_TYPES, PAYMENT_TYPES, TECH_DOC_TYPES, type TechnicianApplication,
} from '../../../api/technicianOnboarding.api';
import { employeesApi } from '../../../api/employees.api';
import { vendorsApi } from '../../../api/vendors.api';
import { extractApiError } from '../../../api/error';

const STEPS = ['Basic', 'Contact', 'Identity', 'Address', 'Skill', 'Deployment', 'Employment', 'Commercial', 'Documents', 'Login', 'Review'];
const cap = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '');
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', verified: 'Verified', approved: 'Approved', active: 'Active',
  rejected: 'Rejected', sent_back: 'Sent Back', clarification_required: 'Clarification Required',
};
type Row = Record<string, unknown>;

export function TechnicianOnboardingForm() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const id = Number(useParams<{ id: string }>().id);
  const queryKey = ['technician-application-edit', id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => technicianOnboardingApi.get(id) });
  const { data: employees } = useQuery({ queryKey: ['employees-lookup'], queryFn: () => employeesApi.list({ limit: 300 }) });
  const { data: vendors } = useQuery({ queryKey: ['vendors-lookup'], queryFn: () => vendorsApi.list({ limit: 300 }) });

  const [form, setForm] = useState<Partial<TechnicianApplication>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => { if (data) setForm(data); }, [data]);

  const editable = form.status === 'draft' || form.status === 'sent_back' || form.status === 'clarification_required';
  const set = (k: keyof TechnicianApplication, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  function payload(autosave: boolean): Record<string, unknown> {
    const f = formRef.current;
    const { id: _id, application_code, status, current_stage, technician_id, login_user_id, workflow, verification_status, approval_status, ...rest } = f as Record<string, unknown>;
    void _id; void application_code; void status; void current_stage; void technician_id; void login_user_id; void workflow; void verification_status; void approval_status;
    return { ...rest, __autosave: autosave };
  }

  const saveMut = useMutation({ mutationFn: (a: boolean) => technicianOnboardingApi.saveDraft(id, payload(a)), onSuccess: (res, a) => { setForm(res); if (!a) setToast('Draft saved'); }, onError: (e) => setToast(extractApiError(e)) });
  const submitMut = useMutation({ mutationFn: () => technicianOnboardingApi.submit(id), onSuccess: (res) => { setForm(res); qc.invalidateQueries({ queryKey }); setSubmitted(true); }, onError: (e) => setToast(extractApiError(e)) });
  const respondMut = useMutation({ mutationFn: () => technicianOnboardingApi.respondClarification(id), onSuccess: (res) => { setForm(res); setToast('Re-submitted'); }, onError: (e) => setToast(extractApiError(e)) });

  useEffect(() => {
    if (!editable) return undefined;
    const t = setInterval(() => saveMut.mutate(true), 120_000);
    return () => clearInterval(t);
  }, [editable]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !form.id) return <Box p={4} textAlign="center"><CircularProgress /></Box>;

  if (submitted) {
    return (
      <>
        <PageHeader title="Technician Onboarding" subtitle={`Application ${form.application_code}`} />
        <Card><CardContent>
          <Alert severity="success" sx={{ mb: 2 }}>Technician application submitted for verification.</Alert>
          <Typography variant="subtitle2" gutterBottom>Recommended actions after creation</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => navigate('/site-applications')}>Assign Site</Button>
            <Button variant="outlined" onClick={() => navigate('/project-applications')}>Assign Project</Button>
            <Button variant="outlined" onClick={() => navigate('/work-orders')}>Create Work Order</Button>
            <Button variant="outlined" onClick={() => navigate('/technician-onboarding-dashboard')}>View Dashboard</Button>
            <Button variant="contained" onClick={() => navigate('/technician-applications')}>Go to Applications</Button>
          </Stack>
        </CardContent></Card>
      </>
    );
  }

  const T = (k: keyof TechnicianApplication, label: string, opts: { type?: string } = {}) => (
    <TextField label={label} type={opts.type ?? 'text'} value={(form[k] as string | number) ?? ''} disabled={!editable}
      onChange={(e) => set(k, opts.type === 'number' ? Number(e.target.value) : e.target.value)} sx={{ minWidth: 220 }} InputLabelProps={opts.type === 'date' ? { shrink: true } : undefined} />
  );
  const Sel = (k: keyof TechnicianApplication, label: string, options: readonly string[]) => (
    <TextField select label={label} value={(form[k] as string) ?? ''} disabled={!editable} onChange={(e) => set(k, e.target.value)} sx={{ minWidth: 220 }}>
      {options.map((o) => <MenuItem key={o} value={o}>{cap(o)}</MenuItem>)}
    </TextField>
  );
  const Lookup = (k: keyof TechnicianApplication, label: string, opts: Array<{ id: number; name: string }>) => (
    <TextField select label={label} value={(form[k] as number) ?? ''} disabled={!editable} onChange={(e) => set(k, Number(e.target.value))} sx={{ minWidth: 220 }}>
      {opts.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  );
  const Bool = (k: keyof TechnicianApplication, label: string) => (
    <FormControlLabel control={<Checkbox checked={Boolean(form[k])} disabled={!editable} onChange={(e) => set(k, e.target.checked)} />} label={label} />
  );
  const emps = (employees?.data ?? []).map((e) => ({ id: e.id, name: e.name }));
  const skills = (form.skill_categories ?? []) as string[];
  const docs = (form.documents ?? []) as unknown as Row[];
  const setDocs = (rows: Row[]) => set('documents', rows);

  return (
    <>
      <PageHeader title="Technician Onboarding" subtitle={`Application ${form.application_code}`}
        action={<Chip color={form.status === 'active' || form.status === 'approved' ? 'success' : form.status === 'rejected' ? 'error' : 'info'} label={STATUS_LABEL[form.status ?? 'draft'] ?? form.status} />} />

      {!editable && form.status !== 'active' && <Alert severity="info" sx={{ mb: 2 }}>This application is <b>{STATUS_LABEL[form.status ?? ''] ?? form.status}</b>. Editing is locked while under review.</Alert>}
      {form.status === 'clarification_required' && <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => respondMut.mutate()}>Re-submit</Button>}>Clarification requested — update and re-submit.</Alert>}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>{STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>

      <Card><CardContent>
        <Stack spacing={2}>
          {activeStep === 0 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <TextField label="Technician ID" value={form.application_code ?? ''} disabled sx={{ minWidth: 200 }} />
              {T('technician_name', 'Technician Name *')}
              {Sel('technician_type', 'Technician Type *', TECH_TYPES)}
              {Sel('gender', 'Gender *', GENDERS)}
              {T('date_of_birth', 'Date of Birth *', { type: 'date' })}
              {T('profile_photo_url', 'Profile Photo URL')}
            </Stack>
          )}
          {activeStep === 1 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('mobile', 'Mobile Number *')}
              {T('alternate_mobile', 'Alternate Mobile')}
              {T('personal_email', 'Personal Email ID')}
              {T('emergency_contact_person', 'Emergency Contact Person *')}
              {T('emergency_contact_number', 'Emergency Contact Number *')}
              {T('emergency_relationship', 'Relationship')}
            </Stack>
          )}
          {activeStep === 2 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('aadhaar', 'Aadhaar Number *')}
              {T('pan_number', 'PAN Number')}
              {T('driving_license_number', 'Driving License Number')}
              {T('state_employee_id', 'State ID / Employee ID')}
              {T('passport_number', 'Passport Number')}
            </Stack>
          )}
          {activeStep === 3 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Current Address</Typography>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {T('current_address_line1', 'Address Line 1 *')}
                {T('current_address_line2', 'Address Line 2')}
                {T('current_state_id', 'State (ID) *', { type: 'number' })}
                {T('current_district', 'District *')}
                {T('current_city', 'City *')}
                {T('current_pincode', 'Pin Code *')}
              </Stack>
              {Bool('permanent_same_as_current', 'Permanent address same as current')}
              {!form.permanent_same_as_current && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">Permanent Address</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    {T('permanent_address_line1', 'Permanent Address')}
                    {T('permanent_state_id', 'State (ID)', { type: 'number' })}
                    {T('permanent_district', 'District')}
                    {T('permanent_city', 'City')}
                    {T('permanent_pincode', 'Pin Code')}
                  </Stack>
                </>
              )}
            </Stack>
          )}
          {activeStep === 4 && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Skill Category * (multiple)</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {SKILL_CATEGORIES.map((s) => (
                    <FormControlLabel key={s} control={<Checkbox size="small" checked={skills.includes(s)} disabled={!editable}
                      onChange={(e) => set('skill_categories', e.target.checked ? [...skills, s] : skills.filter((x) => x !== s))} />} label={cap(s)} />
                  ))}
                </Stack>
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {T('experience_years', 'Experience (Years) *', { type: 'number' })}
                {Sel('experience_level', 'Experience Level *', EXPERIENCE_LEVELS)}
                {Bool('certification_available', 'Certification Available?')}
                {T('certification_details', 'Certification Details')}
              </Stack>
            </Stack>
          )}
          {activeStep === 5 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('vehicle_type', 'Vehicle Type')}
              {Sel('service_coverage', 'Service Coverage *', SERVICE_COVERAGES)}
              {Bool('available_for_travel', 'Available for Travel')}
              {Bool('own_vehicle', 'Own Vehicle Available')}
              {T('joining_date', 'Joining Date', { type: 'date' })}
            </Stack>
          )}
          {activeStep === 6 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {Sel('employment_type', 'Employment Type *', EMPLOYMENT_TYPES)}
              {Lookup('reporting_manager_id', 'Reporting Manager', emps)}
              {Lookup('assigned_vendor_id', 'Assigned Vendor', (vendors?.data ?? []).map((v) => ({ id: v.id, name: v.vendorName })))}
              {T('assigned_region_id', 'Assigned Region (ID)', { type: 'number' })}
            </Stack>
          )}
          {activeStep === 7 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {Sel('payment_type', 'Payment Type *', PAYMENT_TYPES)}
              {T('daily_rate', 'Daily Rate', { type: 'number' })}
              {T('per_site_rate', 'Per Site Rate', { type: 'number' })}
              {T('monthly_salary', 'Monthly Salary', { type: 'number' })}
              {T('bank_name', 'Bank Name')}
              {T('account_number', 'Account Number')}
              {T('ifsc_code', 'IFSC Code')}
              {T('upi_id', 'UPI ID')}
            </Stack>
          )}
          {activeStep === 8 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>Mandatory: Aadhaar, PAN, Photograph, Bank Passbook/Cheque. Optional: Driving License, Experience/Skill Certificate, Police Verification. Files tracked by URL.</Alert>
              <Stack spacing={1.5}>
                {docs.map((row, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField select size="small" label="Type" value={(row.document_type as string) ?? ''} disabled={!editable} sx={{ minWidth: 190 }}
                      onChange={(e) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, document_type: e.target.value } : r)))}>
                      {TECH_DOC_TYPES.map((d) => <MenuItem key={d} value={d}>{cap(d)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="File Name" value={(row.file_name as string) ?? ''} disabled={!editable} onChange={(e) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, file_name: e.target.value } : r)))} sx={{ minWidth: 180 }} />
                    <FileUploadField label="File" disabled={!editable} value={(row.file_url as string) ?? null} onChange={(url) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, file_url: url ?? '' } : r)))} />
                    {editable && <IconButton size="small" color="error" onClick={() => setDocs(docs.filter((_, idx) => idx !== i))}><Delete fontSize="small" /></IconButton>}
                  </Stack>
                ))}
              </Stack>
              {editable && <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setDocs([...docs, { document_type: 'aadhaar' }])}>Add document</Button>}
            </Box>
          )}
          {activeStep === 9 && (
            <Stack spacing={1}>
              {Bool('create_login', 'Create Login Account')}
              <Alert severity="info">If enabled, a username + temporary password are generated on approval and emailed to the technician's personal email.</Alert>
            </Stack>
          )}
          {activeStep === 10 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Review &amp; Submit</Typography>
              <Typography variant="body2" color="text.secondary">Technician: <b>{form.technician_name}</b> · Type: {cap(form.technician_type)} · Skills: {skills.length} · Experience: {form.experience_years ?? '—'}y ({cap(form.experience_level)})</Typography>
              <Typography variant="body2" color="text.secondary">Mobile: {form.mobile ?? '—'} · Aadhaar: {form.aadhaar ?? '—'} · Login: {form.create_login ? 'Yes' : 'No'} · Docs: {docs.length}</Typography>
              {!form.mobile && <Alert severity="warning">Mobile number is required.</Alert>}
              {!form.aadhaar && <Alert severity="warning">Aadhaar is required.</Alert>}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" mt={3}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Previous</Button>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<Close />} color="inherit" onClick={() => navigate('/technician-applications')}>Cancel</Button>
            {editable && <Button startIcon={<Save />} variant="outlined" disabled={saveMut.isPending} onClick={() => saveMut.mutate(false)}>Save Draft</Button>}
            {activeStep < STEPS.length - 1 && <Button variant="contained" onClick={() => { if (editable) saveMut.mutate(true); setActiveStep((s) => s + 1); }}>Next</Button>}
            {activeStep === STEPS.length - 1 && editable && <Button startIcon={<Send />} variant="contained" color="success" disabled={submitMut.isPending} onClick={() => submitMut.mutate()}>Submit</Button>}
          </Stack>
        </Stack>
      </CardContent></Card>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
